import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertCircle, Loader2, UserPlus, GraduationCap } from "lucide-react";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Switch,
  Textarea,
} from "@sportscanner/ui";
import {
  clubCoachingSchema,
  coachProfileUpdateSchema,
  coachSportsSchema,
} from "@sportscanner/schemas";
import { apiGet, apiPut, getApiErrorCode } from "@/lib/api";
import { useMe } from "@/lib/me";
import ClubScopeShell from "@/components/club-scope-shell";

interface Coach {
  id: string;
  name: string;
  email: string;
  image: string | null;
  inviteStatus: string;
  bio: string | null;
  isActive: boolean;
  displayOrder: number | null;
  sports: { sportId: string; sportName: string; pricePerSession: string }[];
}

export default function CoachesPage() {
  const { t } = useTranslation();
  const { user, club, refresh } = useMe();
  const isManager = user?.role === "MANAGER";
  // Managers enable coaching here and the page reflects their own club. The
  // SUPERADMIN manages it per-club via the club picker / Features dialog, so
  // they keep the original always-on layout (no toggle).
  const coachingOn = isManager ? !!club?.allowCoaching : true;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-3xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-neutral-800 mb-1">
            {t("coaches.title")}
          </h1>
          <p className="text-sm text-neutral-500">{t("coaches.subtitle")}</p>
        </div>
        {coachingOn && (
          <Button asChild variant="outline" className="shrink-0">
            <Link to="/invite">
              <UserPlus className="w-4 h-4 mr-1.5" />
              {t("coaches.invite")}
            </Link>
          </Button>
        )}
      </div>

      {isManager && club && (
        <CoachingToggle
          clubId={club.id}
          enabled={!!club.allowCoaching}
          onChanged={refresh}
        />
      )}

      {coachingOn ? (
        <ClubScopeShell>
          {(clubId) => <CoachesManager clubId={clubId} />}
        </ClubScopeShell>
      ) : (
        <p className="text-sm text-neutral-500">{t("coaches.disabledNote")}</p>
      )}
    </div>
  );
}

// Manager-only enable switch for coached sessions. The flag is PRO-tier-gated
// server-side; the sidebar only surfaces this page to clubs whose tier includes
// coaching, so the upgrade error is an edge case (shown as a toast).
function CoachingToggle({
  clubId,
  enabled,
  onChanged,
}: {
  clubId: string;
  enabled: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const mutation = useMutation({
    mutationFn: async (next: boolean) => {
      const parsed = clubCoachingSchema.safeParse({ allowCoaching: next });
      if (!parsed.success) {
        throw {
          error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED",
        };
      }
      await apiPut(`/api/admin/clubs/${clubId}/coaching`, parsed.data);
    },
    onSuccess: async () => {
      await onChanged();
      toast.success(t("common.saved"));
    },
    onError: (err) => {
      toast.error(t(`errors.api.${getApiErrorCode(err)}`));
    },
  });

  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-4 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <Label className="text-sm font-medium text-neutral-800">
          {t("coaches.enableLabel")}
        </Label>
        <p className="text-xs text-neutral-500 mt-0.5">
          {t("coaches.enableHint")}
        </p>
      </div>
      <Switch
        checked={enabled}
        disabled={mutation.isPending}
        onCheckedChange={(v) => mutation.mutate(v)}
      />
    </div>
  );
}

function CoachesManager({ clubId }: { clubId: string }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<Coach | null>(null);

  const coachesQuery = useQuery({
    queryKey: ["admin", "coaches", clubId],
    queryFn: async () => {
      const data = await apiGet<{ coaches: Coach[] }>(
        `/api/admin/clubs/${clubId}/coaches`,
      );
      return data.coaches;
    },
  });

  if (coachesQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }
  if (coachesQuery.isError || !coachesQuery.data) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{t("errors.api.INTERNAL_ERROR")}</AlertDescription>
      </Alert>
    );
  }

  const coaches = coachesQuery.data;

  if (coaches.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-neutral-200 p-10 text-center space-y-3">
        <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto">
          <GraduationCap className="w-6 h-6 text-neutral-400" />
        </div>
        <p className="text-sm text-neutral-500">{t("coaches.empty")}</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {coaches.map((c) => (
          <CoachCard key={c.id} coach={c} onEdit={() => setEditing(c)} />
        ))}
      </div>
      {editing && (
        <CoachEditDialog
          clubId={clubId}
          coach={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function CoachCard({ coach, onEdit }: { coach: Coach; onEdit: () => void }) {
  const { t } = useTranslation();
  const sportLabel = (name: string) =>
    t(`sports.${name}`, { defaultValue: name });
  const pending = coach.inviteStatus === "INVITED";
  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-4 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-neutral-800 truncate">
            {coach.name || coach.email}
          </p>
          {pending && <Badge variant="secondary">{t("coaches.pending")}</Badge>}
          {!coach.isActive && !pending && (
            <Badge variant="secondary">{t("coaches.inactive")}</Badge>
          )}
        </div>
        <p className="text-sm text-neutral-500 truncate">{coach.email}</p>
        {coach.sports.length > 0 && (
          <p className="text-sm text-neutral-600 mt-1.5">
            {coach.sports
              .map((s) => `${sportLabel(s.sportName)} · ${s.pricePerSession}`)
              .join("  ·  ")}
          </p>
        )}
      </div>
      <Button variant="outline" size="sm" onClick={onEdit} className="shrink-0">
        {t("common.edit")}
      </Button>
    </div>
  );
}

function CoachEditDialog({
  clubId,
  coach,
  onClose,
}: {
  clubId: string;
  coach: Coach;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { club } = useMe();
  const clubSports = useMemo(() => club?.sports ?? [], [club]);

  const [isActive, setIsActive] = useState(coach.isActive);
  const [bio, setBio] = useState(coach.bio ?? "");
  // sportId -> { enabled, price (string) }
  const [sportState, setSportState] = useState<
    Record<string, { enabled: boolean; price: string }>
  >(() => {
    const initial: Record<string, { enabled: boolean; price: string }> = {};
    for (const s of clubSports) {
      const existing = coach.sports.find((cs) => cs.sportId === s.id);
      initial[s.id] = {
        enabled: !!existing,
        price: existing ? existing.pricePerSession : "",
      };
    }
    return initial;
  });
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    setErrorCode(null);
  }, [coach.id]);

  const sportLabel = (name: string) =>
    t(`sports.${name}`, { defaultValue: name });

  const mutation = useMutation({
    mutationFn: async () => {
      const profile = coachProfileUpdateSchema.safeParse({
        bio: bio.trim() || null,
        isActive,
      });
      if (!profile.success) {
        throw {
          error: profile.error.issues[0]?.message ?? "VALIDATION_FAILED",
        };
      }
      const sports = clubSports
        .filter((s) => sportState[s.id]?.enabled)
        .map((s) => ({
          sportId: s.id,
          pricePerSession: Number(sportState[s.id]?.price || 0),
        }));
      const sportsParsed = coachSportsSchema.safeParse({ sports });
      if (!sportsParsed.success) {
        throw {
          error: sportsParsed.error.issues[0]?.message ?? "VALIDATION_FAILED",
        };
      }
      await apiPut(
        `/api/admin/clubs/${clubId}/coaches/${coach.id}`,
        profile.data,
      );
      await apiPut(
        `/api/admin/clubs/${clubId}/coaches/${coach.id}/sports`,
        sportsParsed.data,
      );
    },
    onSuccess: async () => {
      toast.success(t("common.saved"));
      await queryClient.invalidateQueries({
        queryKey: ["admin", "coaches", clubId],
      });
      onClose();
    },
    onError: (err) => setErrorCode(getApiErrorCode(err)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{coach.name || coach.email}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {errorCode && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t(`errors.api.${errorCode}`)}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-between gap-3">
            <div>
              <Label className="text-sm font-medium text-neutral-800">
                {t("coaches.activeLabel")}
              </Label>
              <p className="text-xs text-neutral-500 mt-0.5">
                {t("coaches.activeHint")}
              </p>
            </div>
            <Switch checked={isActive} onCheckedChange={setIsActive} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="coach-bio" className="text-sm text-neutral-600">
              {t("coaches.bioLabel")}
            </Label>
            <Textarea
              id="coach-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              className="rounded-lg border-neutral-200"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm text-neutral-600">
              {t("coaches.sportsLabel")}
            </Label>
            <p className="text-xs text-neutral-500">
              {t("coaches.sportsHint")}
            </p>
            <div className="divide-y divide-neutral-100">
              {clubSports.map((s) => {
                const row = sportState[s.id] ?? { enabled: false, price: "" };
                return (
                  <div
                    key={s.id}
                    className="flex items-center justify-between gap-3 py-2.5"
                  >
                    <label className="flex items-center gap-2 text-sm text-neutral-800 cursor-pointer">
                      <Switch
                        checked={row.enabled}
                        onCheckedChange={(v) =>
                          setSportState((prev) => ({
                            ...prev,
                            [s.id]: { ...row, enabled: v },
                          }))
                        }
                      />
                      {sportLabel(s.name)}
                    </label>
                    {row.enabled && (
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        inputMode="numeric"
                        placeholder={t("coaches.pricePlaceholder")}
                        value={row.price}
                        onChange={(e) =>
                          setSportState((prev) => ({
                            ...prev,
                            [s.id]: { ...row, price: e.target.value },
                          }))
                        }
                        className="w-28 rounded-lg border-neutral-200"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              {mutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("common.save")
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
